import { REGISTRATION_FORM_DEFINITION_ID } from "@/constants/constants";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, Button, Container, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormLookups, useFormMetadata } from '@ngt/forms';
import * as React from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Registration, RegistrationSort, RegistrationStatus, Session } from "../../api/dtos";
import DeleteModal from "../../components/common/DeleteModal";
import ExtendedTableCell from "../../components/common/ExtendedTableCell";
import Loading from "../../components/common/Loading";
import { AdminRoutes } from "../../constants/routes";
import { useRegistrationDeskSessions } from "../../hooks/useRegistrationDeskSessions";
import { useRegistrations } from "../../hooks/useRegistrations";
import { getFormRoute } from "../../utils/getComputedFormRoutes";
import AdminNavigation from "./AdminNavigation";
import ExpandingRegoTableRow from "./ExpandingRegoTableRow";





interface IRegistrationPageTableProps {
    registrationDesk?: boolean;
    sort?: RegistrationSort;
    sortDescending?: boolean;
    handleSortClick: (sort: RegistrationSort) => void;
    loading?: boolean;
    registrations?: Registration[];
    handleRegistrationClick: (registrationId: number) => void;
    refreshRegistrations: () => Promise<Registration[] | undefined>;
    tableSessions?: Session[];
    handleDelete: (registration: Registration) => void;
    count?: number;
    rows: number;
    page: number;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeRowsPerPage: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const RegistrationPageTable: React.FunctionComponent<IRegistrationPageTableProps> = ({
    registrationDesk,
    sort,
    sortDescending,
    handleSortClick,
    loading,
    registrations,
    handleRegistrationClick,
    refreshRegistrations,
    tableSessions,
    handleDelete,
    count,
    rows,
    page,
    handleChangePage,
    handleChangeRowsPerPage
}) => {
    const theme = useTheme();

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });

    return (
        <>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} stickyHeader aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {
                                registrationDesk ? 
                                    <TableCell
                                        sx={{
                                            textAlign: 'center'
                                        }}
                                    >
                                        
                                    </TableCell>
                                    :
                                    <>
                                        <ExtendedTableCell
                                            sorted={sort === RegistrationSort.Id}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick(RegistrationSort.Id)}
                                            justify="start"
                                        >
                                            Id
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === RegistrationSort.DateAdded}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick(RegistrationSort.DateAdded)}
                                            justify="start"
                                        >
                                            DateAdded
                                        </ExtendedTableCell>
                                    </>
                            }
                            <ExtendedTableCell
                                sorted={sort === RegistrationSort.LastName}
                                sortDescending={sortDescending}
                                handleClick={() => handleSortClick(RegistrationSort.LastName)}
                                justify="start"
                            >
                                Last Name
                            </ExtendedTableCell>
                            <ExtendedTableCell
                                sorted={sort === RegistrationSort.FirstName}
                                sortDescending={sortDescending}
                                handleClick={() => handleSortClick(RegistrationSort.FirstName)}
                                justify="start"
                            >
                                First Name
                            </ExtendedTableCell>
                            <ExtendedTableCell
                                sorted={sort === RegistrationSort.Institution}
                                sortDescending={sortDescending}
                                handleClick={() => handleSortClick(RegistrationSort.Institution)}
                                justify="start"
                            >
                                Institution
                            </ExtendedTableCell>
                            <ExtendedTableCell
                                sorted={sort === RegistrationSort.DelegateType}
                                sortDescending={sortDescending}
                                handleClick={() => handleSortClick(RegistrationSort.DelegateType)}
                                justify="start"
                            >
                                Registration Type
                            </ExtendedTableCell>
                            <ExtendedTableCell
                                sorted={sort === RegistrationSort.AffiliationRegistration}
                                sortDescending={sortDescending}
                                handleClick={() => handleSortClick(RegistrationSort.AffiliationRegistration)}
                                justify="start"
                            >
                                Affiliation
                            </ExtendedTableCell>
                            {
                                !registrationDesk && (
                                    <>
                                        <ExtendedTableCell
                                            justify="start"
                                        >
                                            Receipt No.
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === RegistrationSort.TotalCost}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick(RegistrationSort.TotalCost)}
                                            justify="center"
                                        >
                                            Total Cost
                                        </ExtendedTableCell>
                                    </>
                                )
                            }
                            <ExtendedTableCell
                                justify="center"
                            >
                                Amount Owing
                            </ExtendedTableCell>
                            {
                                registrationDesk && (
                                    <ExtendedTableCell
                                        justify="center"
                                    >
                                        View Details
                                    </ExtendedTableCell>
                                )
                            }
                            {
                                registrationDesk ?
                                <>
                                    <ExtendedTableCell
                                        justify="center"
                                        sorted={sort === RegistrationSort.Status}
                                        sortDescending={sortDescending}
                                        handleClick={() => handleSortClick(RegistrationSort.Status)}
                                    >
                                        Registered
                                    </ExtendedTableCell>
                                </>
                                :
                                <>
                                    <ExtendedTableCell
                                        justify="center"
                                    >
                                        Delete
                                    </ExtendedTableCell>
                                </>
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            loading ?
                                <Loading />
                                :
                                <>
                                    {
                                        registrationDesk ? 
                                            <>
                                                {
                                                    registrations && registrations?.map((registration, index) => {
                                                        return <ExpandingRegoTableRow 
                                                            key={`registration-sessions-${index}`}
                                                            registration={registration}
                                                            sessions={tableSessions}
                                                            handleMutate={async () => { refreshRegistrations() }} // why does commenting this out make it faster>?>?>???>>
                                                            handleRegistrationClick={handleRegistrationClick}
                                                        />
                                                    })
                                                }
                                            </>
                                        :
                                        <>
                                            {
                                                registrations && registrations?.map((registration) => {
                                                    return <TableRow
                                                        key={`registration-${registration.id}`}
                                                        sx={{
                                                            '&:last-child td, &:last-child th': { border: 0 },
                                                            ":hover": {
                                                                cursor: 'pointer',
                                                                //backgroundColor: registration.sessionAttendance?.every(s => s.attendance !== true) ? noSessionsHover : registration.amountOwing === 0 ? theme.palette.grey[100] : errorLightHover
                                                            },
                                                            //backgroundColor: registration.sessionAttendance?.every(s => s.attendance !== true) ? noSessions : registration.amountOwing === 0 ? 'inherit' : errorLight 
                                                        }}
                                                        onClick={() => handleRegistrationClick(registration.id!)}
                                                    >
                                                        {
                                                            !registrationDesk && (
                                                                <>
                                                                    <TableCell>
                                                                        {registration.invoiceNumber}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            registration?.enteredDate && new Date(registration.enteredDate).toLocaleDateString('en-GB')
                                                                        }
                                                                    </TableCell>
                                                                </>
                                                            )
                                                        }
                                                        <TableCell>
                                                            {registration.lastName}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.firstName}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.institution}
                                                        </TableCell>
                                                        <TableCell>
                                                            {GetFieldLookupItem(lookups, 'delegateType', registration.delegateType)?.value?.toString() ?? registration.delegateType}
                                                        </TableCell>
                                                        <TableCell>
                                                            {GetFieldLookupItem(lookups, 'registrationAffiliation', registration.registrationAffiliation)?.value?.toString() ?? registration.registrationAffiliation}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registration.payments?.length! > 0 ? registration.payments?.[0].receiptNumber : "N/A"}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {registration.cost ? `$${registration.cost}` : '$0'}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {(registration.amountOwing || registration.cost) ? `$${registration.amountOwing ?? registration.cost}` : '$0'}
                                                        </TableCell>
                                                        {/* {
                                                            !notLabels && (
                                                                <TableCell
                                                                    sx={{
                                                                        textAlign: 'center'
                                                                    }}
                                                                >
                                                                    <PrintLabelButton
                                                                    name={`${registration.firstName} ${registration.lastName}`}
                                                                    institution={registration.institution}
                                                                    type={getDelegateType(registration)}
                                                                    printer={printerName}
                                                                    />
                                                                </TableCell>
                                                            )
                                                        } */}
                                                        <TableCell
                                                            onClick={event => {
                                                                handleDelete(registration);
                                                                event.stopPropagation();
                                                            }}
                                                            sx={{
                                                                textAlign: 'center',
                                                                color: theme.palette.primary.main,
                                                                '&:hover':{
                                                                    color: theme.palette.secondary.main,
                                                                    backgroundColor: theme.palette.grey[300]  
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon size="lg" fixedWidth icon={faTrash} />
                                                        </TableCell>
                                                    </TableRow>
                                                })
                                            }
                                        </>
                                    }
                                </>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[20, 50, 100]}
                component="div"
                count={count ?? 0}
                rowsPerPage={rows}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>
    )
}

const MemoizedRegistrationPageTable = memo(RegistrationPageTable);

const RegistrationDeskPage: React.FunctionComponent = () => {

    const navigate = useNavigate();

    const theme = useTheme();

    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(20);

    const [sort, setSort] = useState<RegistrationSort>();
    const [sortDescending, setSortDescending] = useState<boolean>()
    const [search, setSearch] = useState<string>();
    const [tempSearch, setTempSearch] = useState<string>();
    const [regoStatus, setRegoStatus] = useState<RegistrationStatus>();

    const [tableSessions, setTableSessions] = useState<Session[]>();
    const [registration, setRegistration] = useState<Registration | null>();

    React.useEffect(() => {
        const handler = setTimeout(() => setSearch(tempSearch), 300);

        return () => clearTimeout(handler);
    }, [tempSearch])

    const onSearchChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTempSearch(event.target.value)
    }

    const onRegoStatusChange = (event: SelectChangeEvent<RegistrationStatus>) => {
        setRegoStatus(event.target.value as RegistrationStatus);
    }



    const [registrationDesk, setRegistrationDesk] = useState<boolean>(() => {
        return JSON.parse(window.localStorage.getItem('registrationDesk') ?? "false");
    });

    const { registrations, count, loading, refresh: refreshRegistrations } = useRegistrations(page, rows, sort, sortDescending, false, registrationDesk, search, regoStatus);

 


    const sessionData = useRegistrationDeskSessions();

    useEffect(() => {
        if(sessionData?.sessions){
            setTableSessions(sessionData.sessions);
        }
    }, [sessionData])

    const handleNewClick = () => {
        navigate(AdminRoutes.ADMIN_REGISTRATION);
    }

    const handleRegistrationClick = useCallback((id: number) => {
        navigate(getFormRoute(AdminRoutes.ADMIN_REGISTRATION_BY_ID, id.toString()))
    }, [navigate]);

    const handleChangePage = useCallback((event: unknown, newPage: number) => {
        setPage(newPage);
    }, [setPage]);

    const handleSortClick = useCallback((clickSort: RegistrationSort) => {
        if (sort === clickSort) {
            setSortDescending(!sortDescending);
            refreshRegistrations();
        }
        else {
            setSort(clickSort);
            setSortDescending(false);
            refreshRegistrations();
        }
    }, [setSortDescending, setSort, sort, sortDescending])

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRows(parseInt(event.target.value, 10));
        setPage(0);
    }, [setRows, setPage]);

    const handleDelete = useCallback((rego: Registration) => {
        setRegistration(rego)
    }, [setRegistration]);

    const handleClose = (refetch: boolean) => {
        setRegistration(null);

        if (refetch) {
            refreshRegistrations()
        }
    }

    const handleRegistrationDeskChange = () => {
        window.localStorage.setItem('registrationDesk', !registrationDesk ? 'true' : 'false');
        setRegistrationDesk(!registrationDesk);
    }
    
    return (
        <Box>
            <AdminNavigation/>
            <DeleteModal 
                registration={registration}
                handleModalClose={handleClose}
            />
            <Box>
                <Container
                    maxWidth={false}
                >
                    <Box
                        sx={{
                            //...body,
                            px: {
                                xs: 1
                            }
                        }}
                    >
                        <Grid
                            container
                            justifyContent="space-between"
                            sx={{
                                mb: {
                                    xs: 2
                                },
                                mt: {
                                    xs: 5
                                }
                            }}
                        >
                            <Grid 
                                item
                                xs={8}
                            >
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    gutterBottom
                                >
                                    Registrations
                                </Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={2}
                            >
                                <FormControlLabel 
                                    control={ <Switch 
                                            checked={registrationDesk}
                                            onChange={() => handleRegistrationDeskChange()}
                                        /> 
                                    } 
                                    label="Registration Desk"
                                    sx={{
                                        color: registrationDesk ? 'inherit' : theme.palette.grey[500]
                                    }}
                                />
                            </Grid>
                            <Grid 
                                item
                                xs={2}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'end'
                                }}
                            >
                                
                                <Tooltip placement="right" title="New Registration">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleNewClick}
                                        sx={{
                                            p: {
                                                xs: 1
                                            },
                                            minWidth: 48,
                                            maxHeight: 48,
                                            height: '100%',
                                            fontSize: '16px',
                                            borderColor: 'primary.main',
                                            ":hover": {
                                                borderColor: 'secondary.main',
                                                backgroundColor: 'secondary.main',
                                                color: '#FFF'
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} fixedWidth />
                                    </Button>
                                </Tooltip>
                            </Grid>
                            
                            
                        </Grid>
                        <Grid 
                            container
                            spacing={6}
                            sx={{
                                mb: 2
                            }}
                        >
                            <Grid 
                                item
                                xs={12}
                                md={4}
                            >
                                <TextField
                                    sx={{
                                        width: '100%'
                                    }}
                                    onChange={onSearchChange}
                                    variant="outlined"
                                    label="Search for name / Invoice number"
                                />
                            </Grid>
                            <Grid 
                                item
                                xs={12}
                                md={2}
                            >
                                <FormControl fullWidth>
                                    <InputLabel>Registration Status</InputLabel>
                                    <Select
                                        sx={{
                                            width: '100%'
                                        }}
                                        variant="outlined"
                                        label="Registration Status"
                                        onChange={onRegoStatusChange}
                                    >
                                        <MenuItem value={RegistrationStatus.All}>All</MenuItem>
                                        <MenuItem value={RegistrationStatus.Registered}>Registered</MenuItem>
                                        <MenuItem value={RegistrationStatus.Unregistered}>Unregistered</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>


                        </Grid>
                        { sessionData?.loading && <Loading /> }
                        { 
                            !sessionData?.loading && !loading && (!registrations || registrations?.length === 0) && (
                                <Alert
                                    severity="info"
                                    icon={<FontAwesomeIcon fixedWidth icon={faExclamationCircle} />}
                                >
                                    There are no registrations
                                </Alert>
                            )
                        }
                        { 
                            !sessionData?.loading && (loading || (registrations && registrations.length > 0)) && (
                                <MemoizedRegistrationPageTable
                                    count={count}
                                    handleChangePage={handleChangePage}
                                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                                    handleDelete={handleDelete}
                                    handleRegistrationClick={handleRegistrationClick}
                                    handleSortClick={handleSortClick}
                                    page={page}
                                    refreshRegistrations={refreshRegistrations}
                                    registrations={registrations}
                                    rows={rows}
                                    loading={loading}
                                    registrationDesk={registrationDesk}
                                    sort={sort}
                                    sortDescending={sortDescending}
                                    tableSessions={tableSessions}
                                />
                            )
                        }
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

const WrappedRegistrationDeskPage = () => {
    return (
        
        <RegistrationDeskPage />
    )
}

export default WrappedRegistrationDeskPage
